/**
 * Utilities for converting between v1 Message format and v2 UIMessage format
 */

import { v4 as uuidv4 } from 'uuid'
import type { Message as V1Message, ChartData as V1ChartData } from '@/service/api'
import type {
  UIMessage,
  MessagePart,
  TextPart,
  ReasoningPart,
  FilePart,
  AgentId,
  StreamEvent,
  MessageMetadata,
} from '../types'
import {
  AGENT_MENTION_PATTERNS,
  ANY_AGENT_MENTION_PATTERN,
} from '../constants'

// =============================================================================
// V1 to V2 Conversion
// =============================================================================

/**
 * Convert a v1 Message to v2 UIMessage format
 */
export function convertV1ToUIMessage(v1Message: V1Message): UIMessage {
  const parts: MessagePart[] = []

  // Add reasoning part if present
  if (v1Message.thinking) {
    const reasoningPart: ReasoningPart = {
      type: 'reasoning',
      text: v1Message.thinking,
      isCollapsed: true,
    }
    parts.push(reasoningPart)
  }

  // Add main text content
  if (v1Message.content) {
    const textPart: TextPart = {
      type: 'text',
      text: v1Message.content,
    }
    parts.push(textPart)
  }

  // Add chart data as file parts
  if (v1Message.chart_data && v1Message.chart_data.length > 0) {
    for (const chart of v1Message.chart_data) {
      const filePart: FilePart = {
        type: 'file',
        url: chart.url || `data:image/png;base64,${chart.data}`,
        mediaType: chart.type || 'image/png',
        title: chart.title || chart.filename,
        data: chart.data,
      }
      parts.push(filePart)
    }
  }

  // Legacy charts array (URLs)
  if (v1Message.charts && v1Message.charts.length > 0) {
    for (const chartUrl of v1Message.charts) {
      const filePart: FilePart = {
        type: 'file',
        url: chartUrl,
        mediaType: 'image/png',
      }
      parts.push(filePart)
    }
  }

  // Build metadata
  const metadata: MessageMetadata = {}
  if (v1Message.agent_used) {
    metadata.agentUsed = v1Message.agent_used as AgentId
  }
  if (v1Message.feedback !== undefined) {
    metadata.feedback = v1Message.feedback
  }
  if (v1Message.chart_data) {
    metadata.chartData = v1Message.chart_data
  }

  return {
    id: v1Message.id,
    role: v1Message.role,
    parts,
    createdAt: v1Message.timestamp ? new Date(v1Message.timestamp) : new Date(),
    status: v1Message.error ? 'error' : 'ready',
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  }
}

/**
 * Convert an array of v1 Messages to v2 UIMessages
 */
export function convertV1MessagesToUIMessages(v1Messages: V1Message[]): UIMessage[] {
  return v1Messages.map(convertV1ToUIMessage)
}

// =============================================================================
// V2 to V1 Conversion (for backwards compatibility if needed)
// =============================================================================

/**
 * Convert a v2 UIMessage back to v1 Message format
 */
export function convertUIMessageToV1(uiMessage: UIMessage, conversationId: string): V1Message {
  // Extract text content from parts
  const textParts = uiMessage.parts.filter((p): p is TextPart => p.type === 'text')
  const content = textParts.map((p) => p.text).join('\n\n')

  // Extract reasoning/thinking
  const reasoningParts = uiMessage.parts.filter((p): p is ReasoningPart => p.type === 'reasoning')
  const thinking = reasoningParts.map((p) => p.text).join('\n\n') || undefined

  // Extract charts
  const fileParts = uiMessage.parts.filter((p): p is FilePart => p.type === 'file')
  const chartData: V1ChartData[] = fileParts.map((f) => ({
    data: f.data || '',
    filename: f.title || 'chart.png',
    url: f.url,
    type: f.mediaType,
    title: f.title,
  }))

  return {
    id: uiMessage.id,
    conversation_id: conversationId,
    role: uiMessage.role as 'user' | 'assistant',
    content,
    timestamp: uiMessage.createdAt?.toISOString() || new Date().toISOString(),
    thinking,
    chart_data: chartData.length > 0 ? chartData : undefined,
    agent_used: uiMessage.metadata?.agentUsed,
    feedback: uiMessage.metadata?.feedback,
  }
}

// =============================================================================
// Message Creation Helpers
// =============================================================================

/**
 * Create a new user UIMessage
 */
export function createUserMessage(content: string): UIMessage {
  return {
    id: uuidv4(),
    role: 'user',
    parts: [{ type: 'text', text: content }],
    createdAt: new Date(),
    status: 'ready',
  }
}

/**
 * Create a new assistant UIMessage (empty, for streaming)
 */
export function createAssistantMessage(
  id: string,
  agentId?: AgentId
): UIMessage {
  return {
    id,
    role: 'assistant',
    parts: [],
    createdAt: new Date(),
    status: 'streaming',
    metadata: agentId ? { agentUsed: agentId } : undefined,
  }
}

// =============================================================================
// Stream Event Processing
// =============================================================================

/**
 * Apply a stream event to update a UIMessage
 * Returns a new UIMessage with the update applied
 */
export function applyStreamEvent(
  message: UIMessage,
  event: StreamEvent
): UIMessage {
  const newMessage = { ...message, parts: [...message.parts] }

  switch (event.type) {
    case 'message-start':
      // Already handled by createAssistantMessage
      return newMessage

    case 'text-start': {
      // Start a new text part
      const textPart: TextPart = { type: 'text', text: '' }
      newMessage.parts.push(textPart)
      return newMessage
    }

    case 'text-delta': {
      // Append to the last text part, or create one if none exists
      const lastPart = newMessage.parts[newMessage.parts.length - 1]
      if (lastPart?.type === 'text') {
        const updatedParts = [...newMessage.parts]
        updatedParts[updatedParts.length - 1] = {
          ...lastPart,
          text: lastPart.text + event.delta,
        }
        return { ...newMessage, parts: updatedParts }
      } else {
        // No text part exists, create one
        const textPart: TextPart = { type: 'text', text: event.delta }
        newMessage.parts.push(textPart)
        return newMessage
      }
    }

    case 'text-end':
      // Text streaming complete, no action needed
      return newMessage

    case 'reasoning-start': {
      // Start a new reasoning part
      const reasoningPart: ReasoningPart = {
        type: 'reasoning',
        text: '',
        isCollapsed: false,
      }
      newMessage.parts.push(reasoningPart)
      return newMessage
    }

    case 'reasoning-delta': {
      // Find and update the last reasoning part
      const lastReasoningIndex = newMessage.parts.findLastIndex(
        (p) => p.type === 'reasoning'
      )
      if (lastReasoningIndex !== -1) {
        const reasoningPart = newMessage.parts[lastReasoningIndex] as ReasoningPart
        const updatedParts = [...newMessage.parts]
        updatedParts[lastReasoningIndex] = {
          ...reasoningPart,
          text: reasoningPart.text + event.delta,
        }
        return { ...newMessage, parts: updatedParts }
      }
      return newMessage
    }

    case 'reasoning-end': {
      // Collapse reasoning when done
      const lastReasoningIndex = newMessage.parts.findLastIndex(
        (p) => p.type === 'reasoning'
      )
      if (lastReasoningIndex !== -1) {
        const reasoningPart = newMessage.parts[lastReasoningIndex] as ReasoningPart
        const updatedParts = [...newMessage.parts]
        updatedParts[lastReasoningIndex] = {
          ...reasoningPart,
          isCollapsed: true,
        }
        return { ...newMessage, parts: updatedParts }
      }
      return newMessage
    }

    case 'file': {
      const filePart: FilePart = {
        type: 'file',
        url: event.url,
        mediaType: event.mediaType,
        title: event.title,
        data: event.data,
      }
      newMessage.parts.push(filePart)
      return newMessage
    }

    case 'message-end':
      return { ...newMessage, status: 'ready', createdAt: new Date() }

    case 'error':
      return { ...newMessage, status: 'error' }

    default:
      return newMessage
  }
}

// =============================================================================
// Agent Detection
// =============================================================================

/**
 * Detect agent mention in message content
 * Returns the agent ID if found, null otherwise
 */
export function detectAgentMention(content: string): AgentId | null {
  for (const [agentId, pattern] of Object.entries(AGENT_MENTION_PATTERNS)) {
    if (pattern.test(content)) {
      return agentId as AgentId
    }
  }
  return null
}

/**
 * Detect all agent mentions in message content
 * Returns array of agent IDs mentioned
 */
export function detectAllAgentMentions(content: string): AgentId[] {
  const mentions: AgentId[] = []
  const matches = content.matchAll(ANY_AGENT_MENTION_PATTERN)

  for (const match of matches) {
    const mentionText = match[1].toLowerCase()
    // Map mention text to agent ID
    const agentId = mapMentionToAgentId(mentionText)
    if (agentId && !mentions.includes(agentId)) {
      mentions.push(agentId)
    }
  }

  return mentions
}

/**
 * Check if multiple agents are mentioned in content
 */
export function hasMultipleAgentMentions(content: string): boolean {
  return detectAllAgentMentions(content).length > 1
}

/**
 * Map mention text to agent ID
 */
function mapMentionToAgentId(mentionText: string): AgentId | null {
  const lowerMention = mentionText.toLowerCase()
  const mapping: Record<string, AgentId> = {
    'pam': 'pam',
    'ai-plant-support-engineer': 'pam',
    'aice': 'aice',
    'ai-chemical-engineer': 'aice',
    'max': 'max',
    'ai-mechanical-engineer': 'max',
    'ella': 'ella',
    'ai-electrical-engineer': 'ella',
  }
  return mapping[lowerMention] || null
}

/**
 * Map agent ID to V1 backend agent type
 */
export function mapAgentIdToV1(agentId: AgentId): string {
  const mapping: Record<AgentId, string> = {
    pam: 'plant_support_engineer',
    aice: 'chemical_engineer',
    max: 'mechanical_engineer',
    ella: 'electrical_engineer',
  }
  return mapping[agentId] || 'plant_support_engineer'
}

/**
 * Map agent ID to full mention name
 */
export function getAgentMentionName(agentId: AgentId): string {
  const mapping: Record<AgentId, string> = {
    pam: '@ai-plant-support-engineer',
    aice: '@ai-chemical-engineer',
    max: '@ai-mechanical-engineer',
    ella: '@ai-electrical-engineer',
  }
  return mapping[agentId] || '@ai-plant-support-engineer'
}

/**
 * Remove agent mention from message content
 */
export function removeAgentMention(content: string): string {
  return content
    .replace(/@(pam|aice|max|ella|ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer)\s*/gi, '')
    .trim()
}

// =============================================================================
// Message Text Extraction
// =============================================================================

/**
 * Extract plain text content from a UIMessage
 */
export function getMessageText(message: UIMessage): string {
  return message.parts
    .filter((p): p is TextPart => p.type === 'text')
    .map((p) => p.text)
    .join('\n\n')
}

/**
 * Extract reasoning text from a UIMessage
 */
export function getReasoningText(message: UIMessage): string | null {
  const reasoningParts = message.parts.filter(
    (p): p is ReasoningPart => p.type === 'reasoning'
  )
  if (reasoningParts.length === 0) return null
  return reasoningParts.map((p) => p.text).join('\n\n')
}

/**
 * Check if message has any file attachments
 */
export function hasFileAttachments(message: UIMessage): boolean {
  return message.parts.some((p) => p.type === 'file')
}

/**
 * Get file parts from a message
 */
export function getFileParts(message: UIMessage): FilePart[] {
  return message.parts.filter((p): p is FilePart => p.type === 'file')
}

// =============================================================================
// Time Formatting
// =============================================================================

/**
 * Format a date as a time string (e.g., "2:30 PM")
 */
export function formatMessageTime(date: Date | undefined): string {
  if (!date) return ''
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * Format a date relative to now (e.g., "Just now", "5m ago", "Yesterday")
 */
export function formatRelativeTime(date: Date | undefined): string {
  if (!date) return ''

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}
